/**
 * Sezioni "edit" che modificano i campi PROPRI di un Good360 (layer editoriale/curation),
 * non il Good sottostante:
 *  - Descrizione editoriale (description, editorialDescription) → good360Patch
 *  - Prodotti correlati / config (relatedSame*, relatedMax) → good360Patch
 *  - Stato / pubblicazione (BOZZA / PUBBLICATO / ARCHIVIATO) → good360SetStatus
 *
 * Riusano EntityEditFetcher (+ InlineEditForm) come le altre sezioni edit del dialogo 360.
 */

import React, { useState } from 'react';
import { Button } from 'reactstrap';
import { EntityEditFetcher } from './EntityEditFetcher';
import { InlineEditForm } from './CompanyData360EditView';
import { graphqlQuery } from '../utils/graphql';
import { client } from '../utils/client';
import { t } from '../utils/labels';
import { ENTITY } from '../utils/entities';
import { Good360PatchMutation } from '../graphql/mutations/Good360PatchMutation';
import { Good360SetStatusMutation } from '../graphql/mutations/Good360SetStatusMutation';
import { Good360SelfEditQuery } from '../graphql/query/Good360SelfEditQuery';
import Good360Intro from '../static/introspection/Good360.json';
import Good360PatchIntro from '../static/introspection/Good360PatchInput.json';

const good360PatchSection = (
	good360Id: string,
	fields: string[],
	onSaved: () => void,
) => (
	<EntityEditFetcher
		modal={true}
		fetcher={graphqlQuery(Good360SelfEditQuery, { id: good360Id })}
		id={good360Id}
		mutation={Good360PatchMutation}
		entityName={ENTITY.GOOD_360}
		typeIntro={Good360Intro}
		inputTypeIntro={Good360PatchIntro}
		fields={fields}
		onSubmit={() => onSaved()}
	>
		{(args: any) => <InlineEditForm {...args} />}
	</EntityEditFetcher>
);

export const EditorialEditSection: React.FC<{
	good360Id: string;
	onSaved: () => void;
}> = ({ good360Id, onSaved }) =>
	good360PatchSection(good360Id, ['description', 'editorialDescription'], onSaved);

export const RelatedConfigEditSection: React.FC<{
	good360Id: string;
	onSaved: () => void;
}> = ({ good360Id, onSaved }) =>
	good360PatchSection(
		good360Id,
		[
			'relatedSameCategory',
			'relatedSameTrend',
			'relatedSameClaim',
			'relatedSameCompany',
			'relatedMax',
		],
		onSaved,
	);

const STATUS_ACTIONS: Array<{ status: string; labelKey: string; color: string }> = [
	{ status: 'BOZZA', labelKey: 'Good360/status/setBozza', color: 'secondary' },
	{ status: 'PUBBLICATO', labelKey: 'Good360/status/setPubblicato', color: 'success' },
	{ status: 'ARCHIVIATO', labelKey: 'Good360/status/setArchiviato', color: 'warning' },
];

export const StatusEditSection: React.FC<{
	good360Id: string;
	currentStatus: string;
	onSaved: () => void;
}> = ({ good360Id, currentStatus, onSaved }) => {
	const [saving, setSaving] = useState(false);
	const [status, setStatus] = useState(currentStatus);

	const setNewStatus = async (newStatus: string) => {
		setSaving(true);
		try {
			await client.mutate({
				mutation: Good360SetStatusMutation,
				variables: { id: good360Id, newStatus },
			});
			setStatus(newStatus);
			onSaved();
		} finally {
			setSaving(false);
		}
	};

	return (
		<div>
			<p>
				{t`Good360/status/current`}:{' '}
				<strong>{t(['Status360', status, 'label'].join('/'))}</strong>
			</p>
			<div>
				{STATUS_ACTIONS.map((a) => (
					<Button
						key={a.status}
						color={a.color}
						className="margin-right"
						disabled={saving || status === a.status}
						onClick={() => setNewStatus(a.status)}
					>
						{t(a.labelKey)}
					</Button>
				))}
			</div>
		</div>
	);
};

// --- helper per i testi di riepilogo delle card ---

const stripHtml = (html: string): string => {
	if (typeof document === 'undefined') return html;
	const el = document.createElement('div');
	el.innerHTML = html;
	return el.textContent || el.innerText || '';
};

export const editorialSummary = (self: any): string => {
	const d = self && self.description;
	if (!d) return '—';
	const text = stripHtml(d).trim();
	return text.length > 80 ? text.slice(0, 80) + '…' : text || '—';
};

export const relatedSummary = (self: any): string => {
	if (!self) return '—';
	const flags: string[] = [];
	if (self.relatedSameCategory) flags.push(t`Good360/related/category`);
	if (self.relatedSameTrend) flags.push(t`Good360/related/trend`);
	if (self.relatedSameClaim) flags.push(t`Good360/related/claim`);
	if (self.relatedSameCompany) flags.push(t`Good360/related/company`);
	const base = flags.length ? flags.join(', ') : '—';
	return self.relatedMax != null ? `${base} · max ${self.relatedMax}` : base;
};

export const statusSummary = (self: any): string =>
	self && self.status ? t(['Status360', self.status, 'label'].join('/')) : '—';